import React from 'react';
import {
	Button,
	Icon,
	SelectCustom,
	SelectOption,
	Separator,
	Toggle,
	Typography,
} from '@/components/common';
import { Input, InputCheck, Textarea } from '@/components/form';
import { useFieldArray, useForm } from 'react-hook-form';
import Icons from '@/const/icons';
import { Information } from './Information';
import { ModalRequestDocumentPreview } from './ModalRequestDocumentPreview';
import {
	DocumentRequiredPayloadItem,
	RequestDocumentData,
	RequestDocumentItemMap,
} from '@/interfaces';
import { usePathname } from 'next/navigation';

const optionsType: SelectOption[] = [
	{
		label: 'Client Name',
		value: 'CLIENT_NAME',
	},
	{
		label: 'Phone Number',
		value: 'PHONE_NUMBER',
	},
	{
		label: 'Address',
		value: 'ADDRESS',
	},
	{
		label: 'Email',
		value: 'EMAIL',
	},
];

interface Props {
	showMessageBox: boolean;
	isMessageBox: boolean;
	setIsMessageBox: React.Dispatch<React.SetStateAction<boolean>>;
	caseName?: string;
	isCaseReadOnly?: boolean;
	isStepApproved?: boolean;
	onSubmit?: (data: RequestDocumentData) => Promise<void>;
	existingDocuments?: DocumentRequiredPayloadItem[];
	initialData?: DocumentRequiredPayloadItem | null;
	open?: boolean;
}

const INVERT_REQUEST_DOCUMENT_ITEM_MAP: Record<string, string> =
	Object.fromEntries(
		Object.entries(RequestDocumentItemMap).map(([key, value]) => [value, key])
	);

const buildRequestQuestionFromCriteria = (
	criteria: DocumentRequiredPayloadItem['criteria'] = []
) =>
	criteria.map((c) => {
		const fieldType =
			INVERT_REQUEST_DOCUMENT_ITEM_MAP[c.criteria_type] ?? 'PROMPT';
		const isPrompt = fieldType === 'PROMPT';
		return {
			type: isPrompt ? 'AI_PROMPT' : 'FIELD',
			field_type: fieldType,
			field_name: c.prompt ?? '',
			required: c.is_required,
		};
	});

export const RequestDocumentForm: React.FC<Props> = ({
	isMessageBox,
	showMessageBox,
	setIsMessageBox,
	caseName = '',
	isCaseReadOnly = false,
	isStepApproved = false,
	onSubmit: onSubmitProp,
	existingDocuments = [],
	initialData,
	open,
}) => {
	const pathname = usePathname();
	const [isOpenPreview, setIsOpenPreview] = React.useState(false);
	const [emailPreviewContent, setEmailPreviewContent] = React.useState('');
	const [docPreviewName, setDocPreviewName] = React.useState('');
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const {
		register,
		control,
		setValue,
		watch,
		handleSubmit,
		getValues,
		reset,
		formState: { errors },
	} = useForm({ mode: 'onChange' });

	const rules = {
		documentName: {
			required: { value: true, message: 'This field is required' },
			validate: (value: string) =>
				!!value?.trim() || 'Whitespace only is not allowed',
		},
	};

	const { fields, remove, append } = useFieldArray({
		name: 'request_question',
		control,
	});

	React.useEffect(() => {
		if (!open) return;
		if (initialData) {
			reset({
				document_name: initialData.name,
				skip_document: initialData.skip_document,
				request_question: buildRequestQuestionFromCriteria(
					initialData.criteria ?? []
				),
			});
		} else {
			reset({ document_name: '', request_question: [] });
		}
	}, [open, initialData, reset]);

	const documentName = watch('document_name');
	const hasAtLeastOneField =
		fields.length > 0 &&
		fields.some((_field, index) => {
			const fieldType = watch(`request_question.${index}.type`);
			const fieldName = watch(`request_question.${index}.field_name`);
			if (fieldType === 'FIELD') return true;
			if (fieldType === 'AI_PROMPT') return !!fieldName;
			return false;
		});
	const isValid =
		fields.length > 0
			? fields.every((_field, index) => {
					const fieldType = watch(`request_question.${index}.type`);
					const fieldName = watch(`request_question.${index}.field_name`);
					if (fieldType === 'FIELD') {
						return true;
					}
					if (fieldType === 'AI_PROMPT') {
						return !!fieldName;
					}
					return false;
			  }) && hasAtLeastOneField
			: false;

	const getFormattedData = (): RequestDocumentData => {
		const data = getValues();
		const fieldsArr = (data.request_question || []).map((r: any) => ({
			is_required: r.required,
			criteria_type: r.field_type,
			prompt: r.field_name ?? '',
		}));

		return {
			skip_document: data.skip_document,
			document_name: data.document_name?.trim(),
			request_question: fieldsArr,
			message: data.message,
		};
	};

	const onSubmit = async () => {
		try {
			setIsSubmitting(true);
			await onSubmitProp?.(getFormattedData());
			setIsOpenPreview(false);
		} catch (error) {
			console.error(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full ">
				{isMessageBox ? (
					<Textarea
						name="message"
						placeholder="Write the details here"
						title="Insert Message"
						register={register}
						additionalInfoTitle="(Optional)"
					/>
				) : (
					<>
						<div className="overflow-y-auto max-h-[60vh] pr-3">
							<div>
								<Input
									title="Document name"
									name="document_name"
									register={register}
									rules={rules.documentName}
									error={errors.document_name}
									placeholder="Document Name"
									maxLength={70}
									showCount
								/>
								<InputCheck
									name="skip_document"
									className="mt-3"
									classNameInput="!size-5"
									register={register}
								>
									Let customers mark this document as missing so they can keep
									going.
								</InputCheck>
							</div>
							<Separator className="my-5" />
							{/* Instructions */}
							<Information />
							<ul className="space-y-3 mt-3 flex-1">
								{fields.map((item, index) => {
									const fieldType = watch(`request_question.${index}.type`);
									const isRequired = watch(
										`request_question.${index}.required`
									);
									const selectedField = watch(
										`request_question.${index}.field_type`
									);

									return (
										<li
											key={item.id}
											className="flex items-center gap-x-3 w-full bg-gray-20 py-3 px-[18px] rounded-lg"
										>
											<div className="w-full space-y-3">
												<div className="flex justify-between items-center">
													<div className="flex gap-2">
														<Icon
															src={
																fieldType === 'FIELD' ? Icons.coin : Icons.wand
															}
															width={20}
															height={20}
															className="text-primary"
														/>
														<Typography
															type="regular-16"
															className="text-gray-500"
														>
															{fieldType === 'FIELD'
																? 'Database Field'
																: 'AI Prompt'}
														</Typography>
													</div>
													<div className="flex items-center">
														<Typography type="regular-16">
															<Toggle
																isActive={isRequired}
																name={`request_question.${index}.required`}
																register={register}
																setValue={setValue}
															/>{' '}
															{isRequired ? 'Required' : 'Optional'}
														</Typography>
														<button
															type="button"
															onClick={() => remove(index)}
															className="p-2 hover:bg-bg-disabled rounded-full"
														>
															<Icon
																src={Icons.trash}
																width={24}
																height={24}
																className="size-6 text-[#707070]"
															/>
														</button>
													</div>
												</div>
												{fieldType === 'FIELD' && (
													<SelectCustom
														name={`request_question[${index}].field_type`}
														options={optionsType}
														setValueInput={setValue}
														defaultValue={selectedField ?? 'CLIENT_NAME'}
														classNameContainer="w-full"
														className="w-full"
													/>
												)}
												{fieldType === 'AI_PROMPT' && (
													<Textarea
														name={`request_question.${index}.field_name`}
														placeholder="Describe in natural language what the AI should look for... (e.g. 'Extract the vendor's full address usually located at the top left of the document.')"
														register={register}
														className="w-full"
														rows={3}
													/>
												)}
											</div>
										</li>
									);
								})}
							</ul>
						</div>
						<div className="flex flex-col items-center justify-center gap-3">
							<div className="flex items-center justify-center gap-3 w-full">
								<Button
									iconLeft
									icon={Icons.coin}
									type="button"
									label="Add Database Field"
									className="mt-5"
									decoration="line-primary"
									classNameButton="!size-4"
									onClick={() =>
										append({
											type: 'FIELD',
											field_name: '',
											required: false,
											field_type: 'CLIENT_NAME',
										})
									}
								/>
								<Button
									iconLeft
									icon={Icons.plus}
									type="button"
									label="Add AI Prompt"
									className="mt-5"
									decoration="line-primary"
									classNameButton="!size-3"
									onClick={() =>
										append({
											type: 'AI_PROMPT',
											field_name: '',
											required: false,
											field_type: 'PROMPT',
										})
									}
								/>
							</div>
						</div>
					</>
				)}

				{isMessageBox && showMessageBox ? (
					<div className="flex items-center justify-between">
						<Button
							type={'button'}
							decoration="line-primary"
							size="fit"
							className="mt-8"
							disabled={!isValid || !documentName}
							onClick={() => {
								setEmailPreviewContent(watch('message'));
								setDocPreviewName(watch('document_name'));
								setIsOpenPreview(true);
							}}
						>
							Preview
						</Button>

						<Button
							type={'submit'}
							decoration="primary"
							size="fit"
							className="mt-8"
							disabled={!isValid || !documentName || isSubmitting}
						>
							{pathname.includes('/applicant-intake')
								? 'Add Document'
								: 'Send Request'}
						</Button>
					</div>
				) : (
					<Button
						type={showMessageBox ? 'button' : 'submit'}
						decoration="primary"
						className="mt-8"
						disabled={!isValid || !documentName || isSubmitting}
						onClick={() => {
							if (showMessageBox) {
								setIsMessageBox(true);
							}
						}}
					>
						Continue
					</Button>
				)}
			</form>

			<ModalRequestDocumentPreview
				caseName={caseName}
				content={emailPreviewContent}
				modalTitle=""
				isCaseReadOnly={isCaseReadOnly}
				documentList={[
					...existingDocuments.map((doc) => ({
						id: doc.id ?? 0,
						case_id: doc.case_id ?? '',
						name: doc.name,
						skip_document: doc.skip_document,
						created_at: new Date().toISOString(),
					})),
					{
						id: existingDocuments.length + 1,
						case_id: '',
						name: docPreviewName,
						created_at: new Date().toISOString(),
					},
				]}
				open={isOpenPreview}
				setOpen={setIsOpenPreview}
				handleApprove={handleSubmit(onSubmit)}
				isStepApproved={isStepApproved}
			/>
		</>
	);
};
